import React, { useState, useEffect } from 'react';
import { ArrowRight, Target, TrendingUp, Globe, Mail, Phone, ChevronDown, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { portfolioBrands, stats, contactTypes, contactInfo } from '../mock';

export const Home = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'portfolio', 'innovation', 'vision', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="kingsman-landing">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-lg border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <img 
              src="https://customer-assets.emergentagent.com/job_premium-ventures-1/artifacts/q6ku5hk1_KINGSMAN-2.png" 
              alt="Kingsman Group" 
              className="h-12 w-auto"
            />
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('portfolio')} className="nav-link">Portfolio</button>
              <button onClick={() => scrollToSection('innovation')} className="nav-link">Innovation</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <Button className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold">
                Partner With Us
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <video 
          className="hero-bg"
          autoPlay 
          loop 
          muted 
          playsInline
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        >
          <source src="https://customer-assets.emergentagent.com/job_premium-ventures-1/artifacts/5h7igj4s_3141208-uhd_3840_2160_25fps.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
              The Kingsman Group
            </h1>
            <div className="mb-6">
              <p className="text-yellow-500 text-xl md:text-2xl font-semibold tracking-wider uppercase mb-4">
                {contactInfo.tagline}
              </p>
            </div>
            <h2 className="hero-title">
              Building Tomorrow's
              <span className="text-yellow-500"> Category Leaders</span>
            </h2>
            <p className="hero-subtitle">
              A diversified product innovation group bringing transformative brands to market across Australasia.
              We identify opportunity, execute with precision, and build for the long term.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <Button 
                size="lg"
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold text-lg px-8 py-6 h-auto"
                onClick={() => scrollToSection('contact')}
              >
                Investment & Partnerships
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500/10 font-semibold text-lg px-8 py-6 h-auto"
                onClick={() => scrollToSection('portfolio')}
              >
                View Portfolio
              </Button>
            </div>
            <button 
              onClick={() => scrollToSection('about')}
              className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce"
            >
              <ChevronDown className="h-8 w-8 text-yellow-500" />
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="stats-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-bold text-yellow-500 mb-2">{stat.value}</div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">About Kingsman Group</h2>
            <div className="title-underline" />
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">
                Innovation-Driven. Execution-Focused. Built to Scale.
              </h3>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Founded in December 2025, Kingsman Group operates at the intersection of opportunity and execution. 
                We identify high-potential products and categories, then deploy capital, expertise, and strategic 
                partnerships to build sustainable market leaders.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our portfolio spans safety, food, beverage, and lifestyle sectors across New Zealand and Australia. 
                We don't just launch products—we build brands that define categories and deliver lasting value.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/20 p-8">
                <Target className="h-12 w-12 text-yellow-500 mb-4" />
                <h4 className="text-white font-bold text-xl mb-2">Strategic Focus</h4>
                <p className="text-gray-400">Disciplined product selection and market validation</p>
              </Card>
              <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/20 p-8">
                <TrendingUp className="h-12 w-12 text-yellow-500 mb-4" />
                <h4 className="text-white font-bold text-xl mb-2">Scalable Growth</h4>
                <p className="text-gray-400">Systems and partnerships built for expansion</p>
              </Card>
              <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 border-yellow-500/20 p-8 col-span-2">
                <Globe className="h-12 w-12 text-yellow-500 mb-4" />
                <h4 className="text-white font-bold text-xl mb-2">Regional Leadership</h4>
                <p className="text-gray-400">Building category-defining brands across Australasia</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title-dark">Our Portfolio</h2>
            <div className="title-underline-dark" />
            <p className="text-gray-600 text-xl mt-6 max-w-3xl mx-auto">
              Six distinct brands across multiple categories, each positioned for market leadership and sustainable growth.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioBrands.map((brand) => (
              <Card key={brand.id} className="portfolio-card group">
                <div className="relative overflow-hidden">
                  <div 
                    className="h-56 flex items-center justify-center p-8 transition-transform duration-500 group-hover:scale-105"
                    style={{
                      background: brand.transparentBg ? 'transparent' : (brand.logo ? '#ffffff' : `linear-gradient(135deg, ${brand.color}20, ${brand.color}05)`)
                    }}
                  >
                    {brand.logo ? (
                      <img 
                        src={brand.logo} 
                        alt={`${brand.name} logo`}
                        className={`object-contain ${brand.logoSize === 'large' ? 'max-h-48 max-w-full' : 'max-h-full max-w-full'}`}
                      />
                    ) : brand.logoPlaceholder ? (
                      <div className="text-center">
                        <div 
                          className="text-4xl font-bold mb-2"
                          style={{ color: brand.color }}
                        >
                          {brand.name}
                        </div>
                        <div className="text-sm text-gray-500">{brand.category}</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-black/70 text-yellow-500 px-3 py-1 rounded-full text-xs font-semibold">
                      {brand.status}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-500 font-medium mb-2">{brand.category}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{brand.name}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{brand.description}</p>
                  
                  {/* Facebook Link */}
                  {brand.facebookUrl ? (
                    <a 
                      href={brand.facebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                    >
                      <Facebook className="h-4 w-4" />
                      Follow us on Facebook
                    </a>
                  ) : brand.facebookComingSoon ? (
                    <div className="inline-flex items-center gap-2 text-gray-400 text-sm font-medium">
                      <Facebook className="h-4 w-4" />
                      Facebook Coming Soon
                    </div>
                  ) : null}
                  
                  {brand.confidential && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 font-medium">Launching November 2026</p>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation & Growth Section */}
      <section id="innovation" className="section-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Innovation & Growth Strategy</h2>
            <div className="title-underline" />
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-yellow-500 animate-pulse-slow">
                <span className="text-3xl font-bold text-yellow-500">1</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Identify Opportunity</h3>
              <p className="text-gray-300 leading-relaxed">
                We seek products with proven demand, clear differentiation, and scalable business models. 
                Market validation comes first.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-yellow-500 animate-pulse-slow animation-delay-200">
                <span className="text-3xl font-bold text-yellow-500">2</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Execute with Discipline</h3>
              <p className="text-gray-300 leading-relaxed">
                Strategic partnerships, operational excellence, and measured investment ensure each brand reaches its potential.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-yellow-500 animate-pulse-slow animation-delay-400">
                <span className="text-3xl font-bold text-yellow-500">3</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Build for the Long Term</h3>
              <p className="text-gray-300 leading-relaxed">
                We're not chasing trends. We build enduring brands that create value, define categories, and lead markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="section-vision">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-8">Our Vision</h2>
          <p className="text-2xl text-gray-200 leading-relaxed mb-12">
            To be Australasia's most respected product innovation group—known for identifying opportunity, 
            executing with precision, and building brands that last.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div>
              <h4 className="text-xl font-bold text-yellow-500 mb-3">Regional Expansion</h4>
              <p className="text-gray-300">Scaling proven brands across Australia and New Zealand</p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-yellow-500 mb-3">Strategic Partnerships</h4>
              <p className="text-gray-300">Collaborating with investors and distributors who share our vision</p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-yellow-500 mb-3">Category Leadership</h4>
              <p className="text-gray-300">Building brands that define and dominate their markets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-contact">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Let's Build Together</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Whether you're an investor, distributor, or strategic partner, we're interested in conversations that create value.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-lg">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-3 text-gray-700 hover:text-yellow-600 transition-colors font-medium"
              >
                <Mail className="h-6 w-6 text-yellow-500" />
                {contactInfo.email}
              </a>
              <span className="hidden sm:block text-gray-300">|</span>
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-gray-700 hover:text-yellow-600 transition-colors font-medium"
              >
                <Phone className="h-6 w-6 text-yellow-500" />
                {contactInfo.phone}
              </a>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {contactTypes.map((contact, idx) => (
              <Card key={idx} className="contact-card group">
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-yellow-500 transition-colors">
                    <Mail className="h-8 w-8 text-yellow-500 group-hover:text-black transition-colors" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{contact.type}</h3>
                  <p className="text-gray-600 mb-6">{contact.description}</p>
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="inline-flex items-center text-yellow-600 font-semibold hover:text-yellow-700 text-lg"
                  >
                    Get in Touch
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-yellow-500/20 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_premium-ventures-1/artifacts/q6ku5hk1_KINGSMAN-2.png" 
                alt="Kingsman Group" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm mb-4">© 2025 Kingsman Group. All rights reserved.</p>
              <a 
                href="https://www.facebook.com/ThekingsmanNZAU"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-500 transition-colors text-sm font-medium"
              >
                <Facebook className="h-5 w-5" />
                Follow us on Facebook
              </a>
            </div>
            <div className="text-center">
              <p className="text-yellow-500 text-sm font-semibold mb-3">{contactInfo.tagline}</p>
              <p className="text-gray-400 text-sm mb-2">Building tomorrow's category leaders</p>
              <p className="text-gray-500 text-xs">Australia • New Zealand</p>
            </div>
            <div className="text-left md:text-right space-y-2">
              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center justify-start md:justify-end gap-2 text-gray-400 hover:text-yellow-500 transition-colors text-sm"
              >
                <Mail className="h-4 w-4" />
                {contactInfo.email}
              </a>
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-start md:justify-end gap-2 text-gray-400 hover:text-yellow-500 transition-colors text-sm"
              >
                <Phone className="h-4 w-4" />
                {contactInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
